<template>
  <TransitionFadeInOut appear>
    <div :class="['sidebar-container', { compact }]" role="navigation">
      <TransitionFadeInOut>
        <slot />
      </TransitionFadeInOut>
      <div class="toggle-wrapper">
        <FriedIconButton
          v-if="languageLoaded"
          v-tooltip="{
            content: compact ? $t('show_menu_tooltip') : $t('hide_menu_tooltip'),
            distance: 12,
            placement: Placement.Top,
            delay: { show: 300, hide: 100 },
          }"
          class="toggle-slide-button"
          data-external="global-sidebar-toggle-slide-in-out-button"
          @click="toggleSideBar"
        >
          <FriedIcon class="collapse-icon" icon="collapse" />
        </FriedIconButton>
      </div>
    </div>
  </TransitionFadeInOut>
</template>

<script lang="ts">
import { onMounted, defineComponent } from 'vue';
import { storeToRefs } from 'pinia';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { Placement } from '@getaccept/fried-tofu';
import { languageLoaded } from '../../lang';
import { useSidebarStore } from './store/sidebar.store';

export default defineComponent({
  setup() {
    const { isXSmallScreen, isXXSmallScreen } = useBreakpoints();
    const sidebarStore = useSidebarStore();

    const { setCompact, toggleSideBar } = sidebarStore;
    const { compact } = storeToRefs(sidebarStore);

    const setDefaultMenuMode = () => {
      if (isXSmallScreen.value || isXXSmallScreen.value) {
        setCompact(true);
      }
    };

    onMounted(() => {
      setDefaultMenuMode();
      window.addEventListener('resize', setDefaultMenuMode);
    });

    return {
      compact,
      toggleSideBar,
      Placement,
      languageLoaded,
    };
  },
});
</script>

<style lang="scss">
:root {
  --sidebar-min-width: 4rem;
}
</style>
<style lang="scss" scoped>
.sidebar-container {
  width: 14rem;
  height: 100%;
  background: var(--gray-98);
  padding: var(--spacing-100) var(--spacing-50) 0;
  box-shadow: var(--shadow-1);
  z-index: 2;
  transition: 0.2s width ease-in-out;
  position: relative;

  .toggle-wrapper {
    position: absolute;
    right: 0;
    transform: translate3d(50%, -100%, 0);
    top: 50%;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;

    &:hover,
    &:focus-within {
      opacity: 1;
    }
  }

  &:hover {
    .toggle-wrapper {
      opacity: 1;
    }
  }

  @media print {
    display: none;
  }

  .toggle-slide-button {
    border-radius: 50%;
    height: 2.5rem;
    width: 2.5rem;
    background: var(--gray-98);
    color: var(--base-blue);
    box-shadow: var(--shadow-1);

    .collapse-icon {
      transform: rotate(180deg);
    }

    &:hover,
    &:focus {
      background-color: var(--gray-96);
    }

    &:active {
      background: var(--gray-94);
    }
  }

  &.compact {
    width: var(--sidebar-min-width);

    .collapse-icon {
      transform: rotate(0deg);
    }
  }
}
</style>
